import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, apiFetch } from '../../api.js';
import type { Nutrients } from '@kraftwerk/shared';
import { BarcodeScanner, isFoodBarcode } from './BarcodeScanner.js';

export interface FoodSuggestion {
  code: string;
  name: string;
  brand: string | null;
  kcalPer100g: number | null;
  proteinPer100g: number | null;
  carbsPer100g: number | null;
  fatPer100g: number | null;
  portionName: string | null;
  portionGrams: number | null;
  complete: boolean;
  /** Weitere Nährstoffe je 100 g, soweit die Packung sie nennt. Ältere Server: fehlt. */
  nutrients?: Nutrients;
  /** Open Food Facts (Packung) oder BLS (Grundnahrungsmittel); ältere Server: fehlt = off. */
  source?: 'off' | 'bls';
}

type Source = 'off' | 'bls';

type Problem = 'offline' | 'tooMany' | 'unreachable';

/**
 * Ein Lebensmittel finden (seit 21.10.2026 immer offen, Strichcode zuerst):
 * scannen, nach Name oder Strichcode suchen — in Packungen (Open Food Facts)
 * oder Grundnahrungsmitteln (BLS). Ein Treffer **füllt nur das Formular**;
 * gespeichert wird erst nach einem Blick darauf. Unvollständige Treffer
 * stehen als solche da, fehlende Werte bleiben leer.
 *
 * Vorher stand hier „Nährwerte nachschlagen“ über einem leeren Formular,
 * und der Strichcode lag zwei Ebenen tief — man suchte Nährwerte, bevor es
 * ein Lebensmittel gab.
 */
export function FoodLookup({
  onPick,
  onNotFound,
}: {
  onPick: (suggestion: FoodSuggestion) => void;
  /** Ein Strichcode, den Open Food Facts nicht kennt — selbst anlegen, mit Code. */
  onNotFound?: (barcode: string) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const queryId = useId();
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<FoodSuggestion[] | null>(null);
  // Der Strichcode der letzten Suche, wenn sie einer war — für „nicht gefunden“.
  const [searchedCode, setSearchedCode] = useState<string | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [scanning, setScanning] = useState(false);
  // Packung (Open Food Facts) oder Grundnahrungsmittel (BLS 4.0) — beides füllt
  // nur das Formular, mit der Quelle dazu.
  const [source, setSource] = useState<Source>('off');

  const search = (value = query, from: Source = source) => {
    const q = value.trim();
    if (q.length < 2) return;
    setBusy(true);
    setProblem(null);
    setResults(null);
    setSearchedCode(from === 'off' && isFoodBarcode(q) ? q : null);
    const path = from === 'bls' ? '/foods/bls' : '/foods/lookup';
    void apiFetch<{ suggestions: FoodSuggestion[] }>(`${path}?q=${encodeURIComponent(q)}`)
      .then((body) => setResults(body.suggestions))
      .catch((error: unknown) => {
        setProblem(
          !(error instanceof ApiError)
            ? 'offline'
            : error.status === 429
              ? 'tooMany'
              : 'unreachable',
        );
      })
      .finally(() => setBusy(false));
  };

  const value = (number: number | null, unit: string) =>
    number === null
      ? t('lookup.missing')
      : `${number.toLocaleString(i18n.language, { maximumFractionDigits: 1 })} ${unit}`;

  return (
    <div className="flex flex-col gap-3">
      {scanning ? (
        <BarcodeScanner
          onCode={(code) => {
            setScanning(false);
            // Ein Strichcode steht nur auf Packungen.
            setSource('off');
            setQuery(code);
            search(code, 'off');
          }}
          onClose={() => setScanning(false)}
        />
      ) : (
        <button type="button" onClick={() => setScanning(true)} className="kw-btn">
          {t('scan.open')}
        </button>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-1" role="group" aria-label={t('lookup.sourceLabel')}>
          {(['off', 'bls'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={source === option}
              onClick={() => {
                setSource(option);
                setResults(null);
                setProblem(null);
                setSearchedCode(null);
              }}
              className="kw-toggle"
            >
              {t(`lookup.source.${option}`)}
            </button>
          ))}
        </div>
        <label htmlFor={queryId} className="flex flex-col gap-1">
          <span className="kw-label">
            {source === 'bls' ? t('lookup.queryBls') : t('lookup.query')}
          </span>
          <div className="flex gap-2">
            <input
              id={queryId}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                // Das Feld kann in einem Formular stehen: Enter sucht, statt zu speichern.
                if (event.key === 'Enter') {
                  event.preventDefault();
                  search();
                }
              }}
              className="kw-field min-w-0 flex-1"
            />
            <button
              type="button"
              onClick={() => search()}
              disabled={busy || query.trim().length < 2}
              className="kw-btn kw-btn-small shrink-0 px-3"
            >
              {busy ? t('lookup.busy') : t('lookup.search')}
            </button>
          </div>
        </label>
        <p className="text-xs text-ink-muted">
          {source === 'bls' ? t('lookup.hintBls') : t('lookup.hint')}
        </p>
      </div>

      {problem ? (
        <p role="alert" className="text-sm text-danger">
          {t(`lookup.problem.${problem}`)}
        </p>
      ) : null}

      {results && results.length === 0 ? (
        searchedCode ? (
          <div role="status" className="flex flex-col gap-2 text-sm">
            <p>{t('lookup.codeUnknown', { code: searchedCode })}</p>
            {onNotFound ? (
              <button
                type="button"
                onClick={() => onNotFound(searchedCode)}
                className="kw-btn kw-btn-small"
              >
                {t('lookup.createWithCode')}
              </button>
            ) : null}
          </div>
        ) : (
          <p role="status" className="text-sm text-ink-muted">
            {t('lookup.none')}
          </p>
        )
      ) : null}

      {results && results.length > 0 ? (
        <ul className="flex flex-col gap-1" aria-label={t('lookup.results')}>
          {results.map((entry) => (
            <li key={entry.code}>
              <button
                type="button"
                onClick={() => onPick(entry)}
                className="flex w-full flex-col gap-0.5 rounded-xl border border-line px-3 py-2 text-left text-sm"
              >
                <span className="font-semibold break-words">
                  {entry.name}
                  {entry.brand ? (
                    <span className="font-normal text-ink-muted"> · {entry.brand}</span>
                  ) : null}
                </span>
                <span className="text-xs text-ink-muted">
                  {t('lookup.per100g', {
                    kcal: value(entry.kcalPer100g, 'kcal'),
                    protein: value(entry.proteinPer100g, 'g'),
                  })}
                </span>
                {!entry.complete ? (
                  <span className="text-xs text-warn">{t('lookup.incomplete')}</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Quelle, wie sie am Lebensmittel steht — mit Code, Tag und dem Hinweis zum Prüfen. */
export function offSourceNote(code: string, day: string): string {
  return `Open Food Facts ${code}, abgerufen ${day} — mit der Packung vergleichen`;
}

/** BLS-Treffer: Herausgeber, Version, Lizenz und Code (Namensnennung nach CC BY 4.0). */
export function blsSourceNote(code: string): string {
  return `BLS 4.0 (Max Rubner-Institut, CC BY 4.0) ${code} — Durchschnittswerte`;
}

/** Quelle eines Treffers, je nachdem, woher er kommt. */
export function suggestionSourceNote(entry: FoodSuggestion, day: string): string {
  return entry.source === 'bls' ? blsSourceNote(entry.code) : offSourceNote(entry.code, day);
}
